import { cpPorPoblacionId, nombrePoblacionPorId } from "./poblacionesFavoritas";
import { esPrepFotosCortada, etiquetaFotosFaltaPreparar } from "./esPrepFotosCortada";

/** UUID v4 (para no mostrarlo como texto al usuario). */
export function looksLikeUuid(s) {
  if (s == null || s === "") return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(s).trim());
}

/**
 * Texto legible de tipo de inmueble (evita mostrar UUID de columna nativa).
 */
export function tipoInmuebleLegible(captacion, extras = {}) {
  const ex = extras && typeof extras === "object" ? extras : {};
  const esPortalTipo = (v) => {
    const t = String(v || "").trim();
    if (!t || looksLikeUuid(t)) return false;
    if (t.length > 40) return true;
    if (/\bo\b/i.test(t)) return true;
    if (/\ben\s+(venta|alquiler)\b/i.test(t)) return true;
    return false;
  };
  const col = captacion?.tipo_inmueble ?? captacion?.tipo_interes;
  if (col != null && String(col).trim() !== "" && !looksLikeUuid(col) && !esPortalTipo(col)) {
    return String(col).trim();
  }
  const t = ex.tipo_inmueble ?? ex.tipo_interes ?? ex.tipo_inmueble_nombre ?? ex.tipo;
  if (t != null && String(t).trim() !== "" && !looksLikeUuid(String(t)) && !esPortalTipo(t)) {
    return String(t).trim();
  }
  return "";
}

/** Inventario ya relleno (p. ej. desde app en visita). */
export function tieneInventarioMueblesRelleno(inv) {
  if (!inv || typeof inv !== "object") return false;
  const est = inv.estancias;
  if (!Array.isArray(est) || est.length === 0) return false;
  return est.some((e) => Array.isArray(e.items) && e.items.length > 0);
}

/**
 * Importe entero en €: distingue "510000.00" (decimal PG) de "510.000" (miles ES).
 * Quitar solo no-dígitos de "510000.00" → 51000000 (bug que mostraba 51.000.000 €).
 */
export function parsePrecioCard(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const r = Math.round(raw);
    return r > 0 ? r : null;
  }
  let s = String(raw)
    .trim()
    .replace(/€/gi, "")
    .replace(/\s/g, "");
  if (!s) return null;
  s = s.replace(/[^\d.,-]/g, "");
  if (!s || s === "-") return null;

  // 1.234,56 o 510,5 (coma decimal)
  const commaDec = /^([\d.]+),(\d{1,2})$/.exec(s);
  if (commaDec) {
    const intPart = commaDec[1].replace(/\./g, "");
    const n = parseFloat(`${intPart}.${commaDec[2]}`);
    return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
  }

  // Un solo punto y 1–2 cifras finales → decimal (p. ej. 510000.00 de numeric PG)
  const dotDec = /^(\d+)\.(\d{1,2})$/.exec(s);
  if (dotDec) {
    const n = parseFloat(`${dotDec[1]}.${dotDec[2]}`);
    return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
  }

  // Resto: miles con puntos o solo dígitos
  const digits = s.replace(/\D/g, "");
  if (!digits) return null;
  const n = Number(digits);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Precio para tarjetas pipeline (columna API + datos_extras / camelCase). */
export function precioPipelineCard(item, extras = {}) {
  const ex = extras && typeof extras === "object" ? extras : {};
  const raw =
    item.precio_venta ??
    item.precioVenta ??
    ex.precio_venta ??
    ex.precioVenta ??
    ex.precio ??
    ex.precio_publicacion;
  return parsePrecioCard(raw);
}

/** datos_extras de captación (objeto). */
export function datosExtrasCaptacion(captacion) {
  if (typeof captacion?.datos_extras === "object" && captacion.datos_extras !== null) {
    return captacion.datos_extras;
  }
  try {
    return JSON.parse(captacion?.datos_extras || "{}");
  } catch {
    return {};
  }
}

/** Precaptación desde scraping (Ana automática o copia manual al pipeline del agente). */
export function esCaptacionDesdeScraping(captacion) {
  if (!captacion) return false;
  const ex = datosExtrasCaptacion(captacion);
  if (ex.auto_from_scraping === true || ex.auto_from_scraping === "true") return true;
  if (ex.pipeline_from_scraping === true || ex.pipeline_from_scraping === "true") return true;
  const fuente = String(captacion.fuente || "").trim().toLowerCase();
  return fuente.startsWith("scraping ");
}

/** Nombre del responsable en tarjetas pipeline (agente humano o Ana). */
export function agenteResponsableLabel(item) {
  if (!item) return "";
  const agente = item.agente_nombre ? String(item.agente_nombre).trim() : "";
  // Si ya hay compañera asignada (cita), mostrar su nombre aunque quien_trabaja siga en ana.
  if (agente) return agente;
  if (item.quien_trabaja === "ana") return "Ana";
  return "";
}

/** Captación de prueba (ViaFirma sandbox, sin coste por firma). */
export function esCaptacionPrueba(item, extras = {}) {
  const ex = extras && typeof extras === "object" ? extras : {};
  if (ex.captacion_prueba === true || ex.captacion_prueba === "true") return true;
  if (ex.viafirma_entorno === "sandbox") return true;
  if (item?.captacion_prueba === true || item?.captacion_prueba === "true") return true;
  if (item?.viafirma_entorno === "sandbox") return true;
  return false;
}

/** Referencia en tarjeta (columna API o extras / camelCase). */
export function refPipelineCard(item, extras = {}) {
  const ex = extras && typeof extras === "object" ? extras : {};
  const r =
    item.propiedad_ref ??
    item.referencia ??
    item.propiedadRef ??
    ex.referencia ??
    ex.propiedad_ref ??
    ex.ref ??
    ex.referencia_propiedad ??
    ex.referenciaPropiedad;
  if (r == null || String(r).trim() === "") return null;
  return String(r).trim();
}

/** Código INE de municipio (p. ej. 03063), no CP postal. */
export function pareceCodigoInePoblacion(val) {
  return /^\d{4,5}$/.test(String(val || "").trim());
}

/** CP para tarjeta pipeline (columna API, extras o mapa por poblacion_id). */
export function codigoPostalPipelineCard(item, extras = {}) {
  const ex = extras && typeof extras === "object" ? extras : {};
  const directo = String(
    item.codigo_postal ?? ex.codigo_postal ?? ex.cp ?? ex.domicilio_cp ?? "",
  ).trim().replace(/\s/g, "");
  if (directo) return directo;
  const pobId = ex.poblacion_id
    ?? (pareceCodigoInePoblacion(item.poblacion) ? item.poblacion : null)
    ?? (pareceCodigoInePoblacion(ex.poblacion) ? ex.poblacion : null);
  return cpPorPoblacionId(pobId) || "";
}

/** Población legible (no código INE) para tarjeta / modal Pipeline. */
export function poblacionPipelineCard(item, extras = {}) {
  const ex = extras && typeof extras === "object" ? extras : {};
  const nomExtra = String(ex.poblacion_nombre || "").trim();
  if (nomExtra && !pareceCodigoInePoblacion(nomExtra)) return nomExtra;
  const col = String(item.poblacion || "").trim();
  if (col && !pareceCodigoInePoblacion(col)) return col;
  const exPob = String(ex.poblacion || "").trim();
  if (exPob && !pareceCodigoInePoblacion(exPob)) return exPob;
  const mun = String(ex.municipio || "").trim();
  if (mun && !pareceCodigoInePoblacion(mun)) return mun;
  const id = ex.poblacion_id ?? (pareceCodigoInePoblacion(col) ? col : null)
    ?? (pareceCodigoInePoblacion(exPob) ? exPob : null)
    ?? (pareceCodigoInePoblacion(mun) ? mun : null);
  return nombrePoblacionPorId(id) || "";
}

/** Provincia legible (no id «03») para modal Pipeline. */
const PROV_NOMBRE_POR_ID = {
  "03": "Alicante",
  "12": "Castellón",
  "46": "Valencia",
  "08": "Barcelona",
  "28": "Madrid",
  "30": "Murcia",
  "07": "Baleares",
};

export function provinciaPipelineCard(item, extras = {}) {
  const ex = extras && typeof extras === "object" ? extras : {};
  const nom = String(ex.provincia || ex.domicilio_provincia || item.provincia || "").trim();
  if (nom && !/^\d{1,2}$/.test(nom)) return nom;
  const idRaw = String(ex.provincia_id || (/^\d{1,2}$/.test(nom) ? nom : "") || "").trim();
  if (idRaw) {
    const id = idRaw.padStart(2, "0").slice(-2);
    if (PROV_NOMBRE_POR_ID[id]) return PROV_NOMBRE_POR_ID[id];
  }
  const cp = codigoPostalPipelineCard(item, extras);
  if (cp.length >= 2 && /^\d/.test(cp)) {
    const id = cp.slice(0, 2);
    if (PROV_NOMBRE_POR_ID[id]) return PROV_NOMBRE_POR_ID[id];
  }
  return nom || "";
}

/**
 * Piso y puerta para la tarjeta.
 *
 * Sin esto, varias viviendas del mismo edificio (las Violetas 20 de Pego: tres
 * captaciones distintas, puertas 4, 9 y 11) salen con la misma línea y parecen
 * duplicados de la misma captación.
 */
export function pisoPuertaPipelineCard(item, extras = {}) {
  const ex = extras && typeof extras === "object" ? extras : {};
  let piso = String(ex.piso ?? ex.planta ?? item.piso ?? "").trim();
  if (piso) {
    const m2Cand = Number(ex.superficie_m2 ?? item.superficie_m2 ?? ex.m2);
    const nPlanta = parseInt(piso.replace(/\D/g, ""), 10);
    // No pintar m² como planta (p. ej. 90 m² → «90º» / «90ª»)
    if (Number.isFinite(m2Cand) && m2Cand >= 20 && nPlanta === Math.round(m2Cand)) {
      piso = "";
    } else if (/m\s*[²2]|metros?\s*cuadrad|construid|superficie/i.test(piso)) {
      piso = "";
    }
  }
  const puerta = String(ex.puerta ?? item.puerta ?? "").trim();
  const partes = [];
  if (piso) partes.push(piso === "0" ? "bajo" : /^\d+$/.test(piso) ? `${piso}º` : piso);
  if (puerta) partes.push(`pta ${puerta}`);
  return partes.join(" ");
}

/** Línea de dirección en tarjeta pipeline: calle nº, piso pta · CP (o población).
 * Ampliar: si hay polígono/parcela rústica en extras (móvil ya normalizado),
 * mostrarlos aunque `numero` esté vacío.
 */
export function direccionPipelineCard(item, extras = {}) {
  const ex = { ...datosExtrasCaptacion(item), ...extras };
  const calle = String(item.calle || ex.nombre_via || ex.calle || "").trim();
  const numero = String(item.numero || "").trim();
  const poligono = String(ex.poligono || "").trim();
  const parcela = String(ex.parcela || "").trim();
  const cp = codigoPostalPipelineCard(item, extras);
  const pob = poblacionPipelineCard(item, extras);
  let base = calle ? `${calle}${numero ? ` ${numero}` : ""}` : "";
  if (poligono || (parcela && /^\d/.test(parcela))) {
    const rust = [
      poligono ? `Polígono ${poligono}` : null,
      parcela && /^\d/.test(parcela)
        ? (/ y |,/.test(parcela) || parcela.includes(" ") ? `parcelas ${parcela}` : `parcela ${parcela}`)
        : null,
    ].filter(Boolean).join(", ");
    if (rust) base = base ? `${base}, ${rust}` : rust;
  }
  const detalle = pisoPuertaPipelineCard(item, extras);
  if (base && detalle) base = `${base}, ${detalle}`;
  const sufijo = cp || pob || item.poblacion_interes || "";
  if (base && sufijo) return `${base} · ${sufijo}`;
  return base || sufijo || null;
}

function nSeleccionMultimedia(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return 0;
  const ids = Array.isArray(raw.local_ids)
    ? raw.local_ids.map((x) => String(x || "").trim()).filter(Boolean).length
    : 0;
  return Math.max(Number(raw.n) || 0, ids);
}

/**
 * Deber de fotos en tarjeta/puerta.
 * Con meta/selección: alineado a deberFotosDesdeExtras (no suelo multimedia_esperada_*
 * inflado). Sin manifiesto: sí la esperada (legado / aún no declarado).
 */
export function deberFotosCaptacionDisplay(ex, nFotos = 0) {
  const e = ex && typeof ex === "object" ? ex : {};
  const elegidasAgente = Number(e.fotos_elegidas_por_agente) || 0;
  if (elegidasAgente > 0) {
    return Math.max(elegidasAgente, Number(nFotos) || 0);
  }
  const meta = Array.isArray(e.fotos_meta) ? e.fotos_meta : [];
  const uniq = new Set();
  for (const m of meta) {
    const id = String((m && (m.local_id || m.localId)) || "").trim();
    if (id) uniq.add(id);
  }
  const selN = nSeleccionMultimedia(e.seleccion_fotos);
  const base = Math.max(
    selN,
    Number(e.fotos_elegidas) || 0,
    uniq.size,
    Number(nFotos) || 0,
  );
  if (uniq.size > 0 || selN > 0) return base;
  return Math.max(base, Number(e.multimedia_esperada_fotos) || 0);
}

/**
 * Deber de vídeos. Misma regla: con manifiesto no usar multimedia_esperada_videos
 * fantasma (Cándida 9fa19944: 15 en meta/BD, esp=16 → aviso falso 15/16).
 */
export function deberVideosCaptacionDisplay(ex, nVids = 0) {
  const e = ex && typeof ex === "object" ? ex : {};
  const elegidosAgente = Number(e.videos_elegidos_por_agente) || 0;
  if (elegidosAgente > 0) {
    return Math.max(elegidosAgente, Number(nVids) || 0);
  }
  const meta = Array.isArray(e.videos_meta) ? e.videos_meta : [];
  const uniq = new Set();
  for (const m of meta) {
    const id = String((m && (m.local_id || m.localId)) || "").trim();
    if (id) uniq.add(id);
  }
  const selN = nSeleccionMultimedia(e.seleccion_videos);
  const base = Math.max(
    selN,
    Number(e.videos_elegidos) || 0,
    uniq.size,
    Number(nVids) || 0,
  );
  if (uniq.size > 0 || selN > 0) return base;
  return Math.max(base, Number(e.multimedia_esperada_videos) || 0);
}

/** Pérdidas que siguen en meta/selección; las de tandas viejas no cuentan. */
function contarPerdidasVigentesDisplay(lista, ex) {
  const e = ex && typeof ex === "object" ? ex : {};
  const idsDe = (metaKey, selKey) => {
    const s = new Set();
    for (const m of Array.isArray(e[metaKey]) ? e[metaKey] : []) {
      const id = String((m && (m.local_id || m.localId)) || "").trim();
      if (id) s.add(id);
    }
    const sel = e[selKey];
    if (sel && typeof sel === "object" && Array.isArray(sel.local_ids)) {
      for (const id of sel.local_ids) {
        const t = String(id || "").trim();
        if (t) s.add(t);
      }
    }
    return s;
  };
  const idsF = idsDe("fotos_meta", "seleccion_fotos");
  const idsV = idsDe("videos_meta", "seleccion_videos");
  let fotos = 0;
  let videos = 0;
  for (const p of Array.isArray(lista) ? lista : []) {
    if (!p) continue;
    const lid = String(p.local_id || "").trim();
    if (p.tipo === "video") {
      if (!lid || idsV.size === 0 || idsV.has(lid)) videos += 1;
    } else if (!lid || idsF.size === 0 || idsF.has(lid)) {
      fotos += 1;
    }
  }
  return { fotos, videos };
}

/** Piezas en estado terminal (pérdida / no recuperable) en multimedia_piezas_estado. */
function contarTerminalesPiezasDisplay(ex, tipo) {
  const e = ex && typeof ex === "object" ? ex : {};
  const lista = Array.isArray(e.multimedia_piezas_estado) ? e.multimedia_piezas_estado : [];
  const term = new Set(["perdida_confirmada", "no_recuperable_pendiente_confirmacion"]);
  let n = 0;
  for (const p of lista) {
    if (!p || String(p.tipo || "") !== tipo) continue;
    if (term.has(String(p.estado || ""))) n += 1;
  }
  return n;
}

/** Etiqueta de estado multimedia en tarjetas pipeline (Subiendo / incompletas / Procesándose / NAS / vídeo). */
export function etiquetaMultimediaPipeline(item) {
  const ex = datosExtrasCaptacion(item);
  const fase = String(ex.marco_nas_fase || "").trim();
  if (fase === "creando_carpeta") return "Creando carpeta";
  if (fase === "fusionando") return "Fusionando carpetas";
  if (fase === "generando_video") return "Generando vídeo";
  if (ex.procesando_multimedia === true) return "Procesándose";

  const n = Number(item?.fotos_reportaje_count ?? 0);
  const nV = Number(item?.videos_count ?? item?.videos_reportaje_count ?? 0);
  const esp = deberFotosCaptacionDisplay(ex, n);
  const espV = deberVideosCaptacionDisplay(ex, nV);
  // Piezas que el móvil ya ha dado por perdidas: se dejan de esperar, pero la
  // oficina tiene que verlo. Antes esto solo se avisaba con un mensaje pasajero
  // en el móvil del agente y aquí la ficha parecía completa.
  const listaPerdidas = Array.isArray(ex.multimedia_perdidas) ? ex.multimedia_perdidas : [];
  const vigentes = contarPerdidasVigentesDisplay(listaPerdidas, ex);
  const perdidasFotos = vigentes.fotos;
  const perdidasVideos = vigentes.videos;
  // Sin fotos esperadas → OK (p. ej. solo vídeos). Antes exigía n>0 y Ricardo salía «Subiendo fotos».
  const fotosOk = esp <= 0 || n >= esp;
  const videosOk = espV <= 0 || nV >= espV;
  // Ana Benavent 28-oct: secretaría borró fotos en Para Revisar (calidad /
  // verticales) y la tarjeta decía «Fotos incompletas 0/10». El filtro de
  // subida ya pasó (`marco_no_esperar_mas_fotos`); el hueco es editorial, no cola.
  const fotosOkEtiqueta = fotosOk || ex.marco_no_esperar_mas_fotos === true;
  const estadoMm = String(ex.estado_multimedia || "");
  const metaN = Array.isArray(ex.fotos_meta) ? ex.fotos_meta.length : 0;
  const elegF = Number(ex.fotos_elegidas) || 0;
  // Matar app a medias: meta << elegidas y lo declarado ya subió (e36c5799).
  const prepCortada = esPrepFotosCortada(elegF, metaN, n);

  // Solo «N no llegaron» si el hueco coincide EXACTAMENTE con piezas perdidas.
  // Antes era «faltan <= perdidas»: tras bajar esperadas (pieza-perdida esp-1)
  // un hueco residual de 3 con 16 perdidas mostraba «3 fotos no llegaron»
  // mientras faltaban casi todas (Juan 9f928b20).
  // v555: ANTES de «Subiendo por trozo reciente» — un fantasma rechazado
  // seguía sellando multimedia_ultimo_trozo_at y la tarjeta decía
  // «Subiendo vídeos 49/50» con pérdida ya confirmada (6c0fed02).
  const faltanF = Math.max(0, esp - n);
  const faltanV = Math.max(0, espV - nV);
  const termV = contarTerminalesPiezasDisplay(ex, "video");
  const termF = contarTerminalesPiezasDisplay(ex, "foto");
  const perdVEff = Math.max(perdidasVideos, termV);
  const perdFEff = Math.max(perdidasFotos, termF);
  const huecoCubiertoPorPerdida =
    (faltanF > 0 || faltanV > 0)
    && faltanF <= perdFEff
    && faltanV <= perdVEff;
  if ((faltanF || faltanV) && faltanF === perdidasFotos && faltanV === perdidasVideos) {
    return textoPiezasQueNoLlegaron(faltanF, faltanV);
  }
  if (huecoCubiertoPorPerdida) {
    return textoPiezasQueNoLlegaron(faltanF, faltanV);
  }

  // Si están entrando trozos ahora mismo, la subida está viva y eso manda sobre
  // cualquier marca anterior. Con Rita delante, con un vídeo entrando cada
  // minuto, la ficha decía «Vídeos incompletos» porque seguía puesta la marca
  // de estancada de cuando sí estuvo parada (08-09).
  // No aplica si el hueco ya está cubierto por pérdida/terminal (arriba).
  const trozoAt = ex.multimedia_ultimo_trozo_at ? new Date(ex.multimedia_ultimo_trozo_at).getTime() : 0;
  // Misma ventana que la cola viva en servidor (15 min). 5 min hacía «incompletos»
  // a mitad de un vídeo PRO a ~0,4 Mbps.
  const subiendoAhora = trozoAt > 0 && Date.now() - trozoAt < 15 * 60 * 1000;
  if (subiendoAhora && !(fotosOkEtiqueta && videosOk)) {
    if (!fotosOkEtiqueta && !videosOk && esp > 0 && espV > 0) {
      return `Subiendo ${n}/${esp} · ${nV}/${espV}`;
    }
    if (!fotosOkEtiqueta && videosOk && esp > 0 && espV > 0) {
      return `Subiendo fotos ${n}/${esp}`;
    }
    if (!fotosOkEtiqueta && esp > 0) return `Subiendo fotos ${n}/${esp}`;
    if (!videosOk && espV > 0) return `Subiendo vídeos ${nV}/${espV}`;
  }
  // Antes: si n>=esp y quedaban perdidas stale → «1 foto no llegó» falso (Juan v442).
  // Ampliar: solo «no llegó» con hueco real (rama exacta arriba). Reconcile limpia
  // local_ids ya en BD; Albenull con pieza ausente sigue coincidiendo faltan===perdidas.

  if (estadoMm === "fotos_incompletas" || ex.multimedia_fotos_estancada === true) {
    if (fotosOkEtiqueta && videosOk) return null;
    if (fotosOkEtiqueta) {
      // Solo hueco de vídeos: no etiquetar como «Fotos incompletas».
      if (!videosOk && espV > 0) return `Vídeos incompletos ${nV}/${espV}`;
      return null;
    }
    if (prepCortada) return etiquetaFotosFaltaPreparar(n, elegF);
    if (esp > 0 && !videosOk && espV > 0) return `Fotos ${n}/${esp} · vídeos ${nV}/${espV}`;
    if (esp > 0) return `Fotos incompletas ${n}/${esp}`;
    return "Fotos incompletas";
  }
  if (estadoMm === "videos_incompletos" || ex.multimedia_videos_estancada === true) {
    if (fotosOkEtiqueta && videosOk) return null;
    if (espV > 0 && !fotosOkEtiqueta && esp > 0) return `Vídeos ${nV}/${espV} · fotos ${n}/${esp}`;
    if (espV > 0) return `Vídeos incompletos ${nV}/${espV}`;
    return "Vídeos incompletos";
  }

  if (estadoMm === "subida_dormida" || ex.subida_dormida === true) {
    if (fotosOkEtiqueta && videosOk) return null;
    if (fotosOkEtiqueta && !videosOk && espV > 0) {
      return `Vídeos incompletos ${nV}/${espV}`;
    }
    if (fotosOkEtiqueta) return null;
    if (esp > 0 || espV > 0) {
      return `Subida dormida ${n}/${esp || "?"} · ${nV}/${espV || "?"}`;
    }
    return "Subida dormida";
  }

  if (ex.subida_multimedia_pendiente === true) {
    if (fotosOkEtiqueta && videosOk) return null;
    // Verdad UI: si no ha llegado NADA y la ficha lleva >12 min, no fingir «Subiendo».
    //
    // La edad se cuenta desde la última tanda declarada, no desde que se creó la
    // captación. Al retomar una ficha de hace días y añadirle vídeos, la de
    // Ricardo salía «incompletos» nada más empezar a subir, con la tanda
    // entrando en ese momento.
    const createdMs = item?.created_at ? new Date(item.created_at).getTime() : 0;
    const declMs = ex.videos_meta_declared_at ? new Date(ex.videos_meta_declared_at).getTime() : 0;
    // Un trozo recién recibido es la prueba de que la subida está viva; la
    // declaración de la cola se hace una vez por tanda y puede ser de ayer.
    const trozoMs = ex.multimedia_ultimo_trozo_at ? new Date(ex.multimedia_ultimo_trozo_at).getTime() : 0;
    const refMs = Math.max(
      createdMs,
      Number.isFinite(declMs) ? declMs : 0,
      Number.isFinite(trozoMs) ? trozoMs : 0,
    );
    const ageMin = refMs > 0 ? (Date.now() - refMs) / 60000 : 0;
    if (!fotosOkEtiqueta && esp > 0 && n === 0 && ageMin >= 12) {
      return `Fotos incompletas 0/${esp}`;
    }
    if (fotosOkEtiqueta && !videosOk && espV > 0 && nV === 0 && ageMin >= 12) {
      return `Vídeos incompletos 0/${espV}`;
    }
    if (!fotosOkEtiqueta && esp > 0 && n < esp && ageMin >= 20) {
      return `Fotos incompletas ${n}/${esp}`;
    }
    if (fotosOkEtiqueta && !videosOk && espV > 0 && nV < espV && ageMin >= 20) {
      return `Vídeos incompletos ${nV}/${espV}`;
    }
    // Sin trozo reciente la cola está parada: no fingir «Subiendo»
    // (Magallanes 09-09: etiqueta viva con 7/47 congelados).
    // Ampliar: solo a partir de 15 min, no a los 5 min con n>0 (un vídeo lento).
    if (!subiendoAhora) {
      if (esp > 0 && !fotosOkEtiqueta && (n > 0 || ageMin >= 12) && ageMin >= 15) {
        if (prepCortada) return etiquetaFotosFaltaPreparar(n, elegF);
        return `Fotos incompletas ${n}/${esp}`;
      }
      if (espV > 0 && !videosOk && (nV > 0 || ageMin >= 12) && ageMin >= 15) {
        return `Vídeos incompletos ${nV}/${espV}`;
      }
    }
    // Con el avance a la vista: texto corto para que quepa en la tarjeta.
    if (!fotosOkEtiqueta && !videosOk && esp > 0 && espV > 0) {
      return `Subiendo ${n}/${esp} · ${nV}/${espV}`;
    }
    if (!fotosOkEtiqueta && videosOk && esp > 0) {
      return `Subiendo fotos ${n}/${esp}`;
    }
    if (fotosOkEtiqueta && espV > 0 && !videosOk) return `Subiendo vídeos ${nV}/${espV}`;
    if (estadoMm === "subiendo_videos") return "Subiendo vídeos";
    if (esp > 0 && !fotosOkEtiqueta) return `Subiendo fotos ${n}/${esp}`;
    if (fotosOkEtiqueta && videosOk) return null;
    return "Subiendo fotos";
  }
  // Sin hueco real no mostrar «no llegó» por lista stale (Juan v442).
  return null;
}

/** «No llegó» y no «incompleta»: la subida acabó, ese hueco hay que repetirlo. */
function textoPiezasQueNoLlegaron(fotos, videos) {
  if (fotos && videos) return `${fotos + videos} piezas no llegaron`;
  if (videos) return videos === 1 ? "1 vídeo no llegó" : `${videos} vídeos no llegaron`;
  if (fotos) return fotos === 1 ? "1 foto no llegó" : `${fotos} fotos no llegaron`;
  return null;
}

/**
 * Qué multimedia falta de verdad, para que la puerta y la tarjeta digan lo mismo.
 *
 * El Pipeline cerraba la entrada mirando solo `subida_multimedia_pendiente` y la
 * tarjeta decidía la etiqueta con los contadores. El 08-09 discreparon: la
 * captación de Ricardo tenía las 50 fotos y los 14 vídeos declarados, la tarjeta
 * no mostraba nada y aun así el CRM contestaba «se están subiendo las fotos».
 * Puerta cerrada, tarjeta muda y encima hablando de fotos cuando lo que subía
 * eran vídeos.
 *
 * Devuelve null cuando ha llegado todo lo declarado: si la tarjeta calla, se entra.
 * También null si lo que falta ya está en `multimedia_perdidas` (oficina asume la
 * pérdida): la tarjeta dice «N foto(s) no llegaron» y bloquear pediría reenviar
 * lo que ya se dio por perdido (Mari Cruz 09-09).
 */
export function multimediaPendienteRevision(item) {
  const ex = datosExtrasCaptacion(item);

  const n = Number(item?.fotos_reportaje_count ?? 0);
  const nV = Number(item?.videos_count ?? item?.videos_reportaje_count ?? 0);
  const esp = deberFotosCaptacionDisplay(ex, n);
  const espV = deberVideosCaptacionDisplay(ex, nV);
  const listaPerdidas = Array.isArray(ex.multimedia_perdidas) ? ex.multimedia_perdidas : [];
  const vigentes = contarPerdidasVigentesDisplay(listaPerdidas, ex);
  const perdidasFotos = Math.max(vigentes.fotos, contarTerminalesPiezasDisplay(ex, "foto"));
  const perdidasVideos = Math.max(vigentes.videos, contarTerminalesPiezasDisplay(ex, "video"));
  const faltanF = Math.max(0, (Number.isFinite(esp) ? esp : 0) - (Number.isFinite(n) ? n : 0));
  const faltanV = Math.max(0, (Number.isFinite(espV) ? espV : 0) - (Number.isFinite(nV) ? nV : 0));

  // Hueco cubierto por piezas dadas por perdidas → misma verdad que la etiqueta.
  if ((faltanF > 0 || faltanV > 0) && faltanF <= perdidasFotos && faltanV <= perdidasVideos) {
    return null;
  }

  const fotosOk = !Number.isFinite(esp) || esp <= 0 || (Number.isFinite(n) && n >= esp)
    || ex.marco_no_esperar_mas_fotos === true;
  const videosOk = !Number.isFinite(espV) || espV <= 0 || (Number.isFinite(nV) && nV >= espV);
  if (fotosOk && videosOk) return null;

  const estadoMm = String(ex.estado_multimedia || "");
  const fotosRotas = estadoMm === "fotos_incompletas" || ex.multimedia_fotos_estancada === true;
  const videosRotos = estadoMm === "videos_incompletos" || ex.multimedia_videos_estancada === true;
  const nTxt = Number.isFinite(n) ? String(n) : '?';
  const espTxt = Number.isFinite(esp) && esp > 0 ? String(esp) : '?';
  const nVTxt = Number.isFinite(nV) ? String(nV) : '?';
  const espVTxt = Number.isFinite(espV) && espV > 0 ? String(espV) : '?';
  // Misma ventana que la tarjeta: un trozo en los últimos 5 min = subida viva.
  // Sin eso la tarjeta dice «incompletas» y el modal sigue «se están subiendo» (Ángel 17/56).
  const trozoAt = ex.multimedia_ultimo_trozo_at ? new Date(ex.multimedia_ultimo_trozo_at).getTime() : 0;
  const subiendoAhora = trozoAt > 0 && Date.now() - trozoAt < 5 * 60 * 1000;

  if (!fotosOk) {
    if (subiendoAhora && !fotosRotas) {
      return {
        duro: true,
        mensaje: `Todavía se están subiendo las fotos desde el móvil (${nTxt} de ${espTxt}). Espera a que terminen antes de revisar.`,
      };
    }
    return {
      duro: false,
      mensaje: `Faltan fotos del móvil (${nTxt} de ${espTxt}) y la subida está parada.\n\n¿Quieres revisarla igualmente?`,
    };
  }

  // Las fotos están; solo faltan vídeos. Se avisa con el avance real y se deja
  // decidir: quedarse fuera esperando un vídeo no es operativo.
  return {
    duro: false,
    mensaje: videosRotos
      ? `Faltan vídeos del móvil (${nVTxt} de ${espVTxt}) y la subida está parada. Las fotos sí están completas.\n\n¿Quieres revisarla igualmente?`
      : `Se están subiendo los vídeos desde el móvil (${nVTxt} de ${espVTxt}). Las fotos ya están completas.\n\n¿Quieres revisarla igualmente?`,
  };
}

/**
 * Origen multimedia de la captación (móvil vs CRM escritorio).
 * Ampliar: señales de curación Marco cuentan como móvil aunque el flag se haya pisado a escritorio.
 */
export function origenMultimediaCaptacion(item) {
  const ex = datosExtrasCaptacion(item);
  if (ex.origen_multimedia === "movil") return "movil";
  // Flujo móvil con selección Vision/Marco (el flag a veces se sobrescribe al guardar ficha)
  if (
    ex.seleccion_marco_estado ||
    ex.prioridad_seleccion === "marco" ||
    ex.prioridad_seleccion === "todas" ||
    ex.subida_multimedia_pendiente === true ||
    ex.ficha_finalizada_pendiente_multimedia === true
  ) {
    return "movil";
  }
  if (ex.origen_multimedia === "escritorio") return "escritorio";
  return null;
}

/** Texto corto para la cabecera plegable del inventario. */
export function resumenInventarioMuebles(inv) {
  if (!tieneInventarioMueblesRelleno(inv)) {
    return "Plegado — despliega para generar con IA o editar a mano";
  }
  const nEst = inv.estancias.length;
  const nItems = inv.estancias.reduce(
    (s, e) => s + (Array.isArray(e.items) ? e.items.length : 0),
    0
  );
  return `${nEst} estancia(s) · ${nItems} ítem(s) — pulsa para ver o editar`;
}
